import React from 'react';
import { useNavigate } from 'react-router-dom';
import './TopPicksRow.css';
import { FaCode, FaBriefcase, FaCertificate, FaProjectDiagram, FaFileAlt, FaBook, FaPenNib, FaAddressBook, FaHeart } from 'react-icons/fa';

type ProfileType = 'recruiter' | 'researcher' | 'stalker' | 'explorer';

interface TopPicksRowProps {
  profile: ProfileType;
}

const topPicksConfig = {
  recruiter: [
    { title: "Skills", imgSrc: "/assets/images/picsum_seed_skills.jpg", icon: <FaCode aria-hidden="true" />, route: "/skills", description: "Technical skills and expertise" },
    { title: "Experience", imgSrc: "/assets/images/picsum_seed_workexperience.jpg", icon: <FaBriefcase aria-hidden="true" />, route: "/work-experience", description: "Professional work history" },
    { title: "Certifications", imgSrc: "/assets/images/picsum_seed_certifications.jpg", icon: <FaCertificate aria-hidden="true" />, route: "/certifications", description: "Professional certifications" },
    { title: "Projects", imgSrc: "/assets/images/picsum_seed_projects.jpg", icon: <FaProjectDiagram aria-hidden="true" />, route: "/projects", description: "Portfolio projects" },
    { title: "Resume", imgSrc: "/assets/images/picsum_seed_contact.jpg", icon: <FaFileAlt aria-hidden="true" />, route: "/resume", description: "Download resume" },
    { title: "Contact", imgSrc: "/assets/images/picsum_seed_networking.jpg", icon: <FaAddressBook aria-hidden="true" />, route: "/contact", description: "Get in touch" }
  ],
  researcher: [
    { title: "Skills", imgSrc: "/assets/images/picsum_seed_coding.jpg", route: "/skills", icon: <FaCode aria-hidden="true" />, description: "Technical expertise" },
    { title: "Projects", imgSrc: "/assets/images/picsum_seed_development.jpg", route: "/projects", icon: <FaProjectDiagram aria-hidden="true" />, description: "Research projects" },
    { title: "Certifications", imgSrc: "/assets/images/picsum_seed_badge.jpg", route: "/certifications", icon: <FaCertificate aria-hidden="true" />, description: "Credentials" },
    { title: "Experience", imgSrc: "/assets/images/picsum_seed_work.jpg", route: "/work-experience", icon: <FaBriefcase aria-hidden="true" />, description: "Work history" },
    { title: "Resume", imgSrc: "/assets/images/picsum_seed_connect.jpg", route: "/resume", icon: <FaFileAlt aria-hidden="true" />, description: "CV download" },
    { title: "Reading", imgSrc: "/assets/images/picsum_seed_books.jpg", route: "/reading", icon: <FaBook aria-hidden="true" />, description: "Book recommendations" }
  ],
  stalker: [
    { title: "Contact", imgSrc: "/assets/images/picsum_seed_call.jpg", route: "/contact", icon: <FaAddressBook aria-hidden="true" />, description: "Contact information" },
    { title: "Resume", imgSrc: "/assets/images/picsum_seed_resume.jpg", route: "/resume", icon: <FaFileAlt aria-hidden="true" />, description: "Resume download" },
    { title: "Projects", imgSrc: "/assets/images/picsum_seed_planning.jpg", route: "/projects", icon: <FaProjectDiagram aria-hidden="true" />, description: "Project portfolio" },
    { title: "Experience", imgSrc: "/assets/images/picsum_seed_work.jpg", route: "/work-experience", icon: <FaBriefcase aria-hidden="true" />, description: "Career timeline" },
    { title: "Skills", imgSrc: "/assets/images/picsum_seed_coding.jpg", route: "/skills", icon: <FaCode aria-hidden="true" />, description: "Technical skills" },
    { title: "Interests", imgSrc: "/assets/images/picsum_seed_music.jpg", route: "/interests", icon: <FaHeart aria-hidden="true" />, description: "Personal interests" }
  ],
  explorer: [
    { title: "Reading", imgSrc: "/assets/images/picsum_seed_books.jpg", route: "/reading", icon: <FaBook aria-hidden="true" />, description: "Recommended books" },
    { title: "Blogs", imgSrc: "/assets/images/picsum_id_1027.jpg", route: "/blogs", icon: <FaPenNib aria-hidden="true" />, description: "Blog posts" },
    { title: "Projects", imgSrc: "/assets/images/picsum_seed_innovation.jpg", route: "/projects", icon: <FaProjectDiagram aria-hidden="true" />, description: "Creative projects" },
    { title: "Interests", imgSrc: "/assets/images/picsum_seed_music.jpg", route: "/interests", icon: <FaHeart aria-hidden="true" />, description: "Hobbies and interests" },
    { title: "Resume", imgSrc: "/assets/images/picsum_seed_connect.jpg", route: "/resume", icon: <FaFileAlt aria-hidden="true" />, description: "Professional resume" },
    { title: "Certifications", imgSrc: "/assets/images/picsum_seed_medal.jpg", route: "/certifications", icon: <FaCertificate aria-hidden="true" />, description: "Achievements" }
  ]
};


const TopPicksRow: React.FC<TopPicksRowProps> = ({ profile }) => {
  const navigate = useNavigate();
  const topPicks = topPicksConfig[profile];

  const profileDisplayNames: Record<ProfileType, string> = {
    recruiter: 'Recruiter',
    researcher: 'Researcher',
    stalker: 'Stalker',
    explorer: 'Explorer'
  };

  const handleKeyDown = (e: React.KeyboardEvent, route: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      window.scrollTo(0, 0);
      navigate(route);
    }
  };

  return (
    <section className="top-picks-row" aria-labelledby="top-picks-heading">
      <h2 id="top-picks-heading" className="row-title">
        Today's Top Picks for {profileDisplayNames[profile]}
      </h2>
      <div className="card-row" role="list" aria-label="Portfolio sections">
        {topPicks.map((pick, index) => (
          <article
            key={index}
            className="pick-card"
            onClick={() => {
              window.scrollTo(0, 0);
              navigate(pick.route);
            }}
            onKeyDown={(e) => handleKeyDown(e, pick.route)}
            style={{ animationDelay: `${index * 0.2}s` }}
            role="listitem"
            tabIndex={0}
            aria-label={`${pick.title}: ${pick.description}`}
          >
            <img
              src={pick.imgSrc}
              alt={`${pick.title} - ${pick.description}`}
              className="pick-image"
              loading="lazy"
              width="300"
              height="180"
            />
            <div className="overlay" aria-hidden="true">
              <div className="pick-label">{pick.title}</div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default TopPicksRow;
